import React from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/browser";
import Amplify, { Auth, API } from "aws-amplify";
import queryString from "query-string";
import "./index.css";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import { AppSyncApolloProvider } from "./Apollo";
import AppUser from "./AppUser";
import { Loader } from "semantic-ui-react";
import UserContext from "contexts/UserContext";
import { useQuery } from "@apollo/client";
import { GET_MY_USER } from "./GraphQL";
import "@aws-amplify/ui-react/styles.css";
import "semantic-ui-css/semantic.min.css";
import {
  Authenticator,
  Image,
  Button as UIButton,
  View,
  Text,
  useTheme,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import { Message, Button, Header, Icon } from "semantic-ui-react";
import aws_exports from "./aws-exports";
import fullLogoImg from "./full-logo.png";

Sentry.init({
  dsn: "https://7d02747fa0c746d4949277d836a03def@sentry.io/2384339",
});

Amplify.configure(aws_exports);

Amplify.configure({
  API: {
    endpoints: [
      {
        name: "KnowMyDayAPI",
        endpoint: "https://api.knowmyday.com",
      },
    ],
  },
});

// No offline support
serviceWorker.unregister();

const getUsername = async (email) => {
  const apiName = "KnowMyDayAPI";
  const path = `/user/${email}`;
  return await API.get(apiName, path);
};

const AppWithUser = ({ signOut, user }) => {
  console.log("AppWithUser", user);
  const { loading, error, data } = useQuery(GET_MY_USER);
  if (loading) {
    return <Loader active />;
  }
  if (error) {
    throw error;
  }
  const appUser = new AppUser(user);
  appUser.setAttributes(data.myProfile);

  Sentry.configureScope(function (scope) {
    scope.setUser({ email: appUser.getAttributes().email });
  });

  return (
    <UserContext.Provider value={appUser}>
      <App />
    </UserContext.Provider>
  );
};

const params = queryString.parse(window.location.search);
const initialState = params.authState || "signIn";

// Safe JSON parsing with URL decoding fallback
let clientMetadata = {};
if (params.clientMetadata) {
  try {
    clientMetadata = JSON.parse(params.clientMetadata);
  } catch (error) {
    // Try decoding if it's URL-encoded
    try {
      const decoded = decodeURIComponent(params.clientMetadata);
      clientMetadata = JSON.parse(decoded);
    } catch (decodeError) {
      console.error("Failed to parse clientMetadata:", error, params.clientMetadata);
      Sentry.captureException(error, {
        extra: { clientMetadata: params.clientMetadata }
      });
      // Default to empty object - allows app to continue loading
    }
  }
}

const defaultEmail = params.email || "";

const getComponents = (invited) => ({
  Header() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Image
          alt="Know My Day Logo"
          src={fullLogoImg}
          width={180}
          height={180}
        />
      </View>
    );
  },
  Footer() {
    const { tokens } = useTheme();
    return (
      <View textAlign="center" padding={tokens.space.small}>
        <Text color="neutral.40">{process.env.REACT_APP_VERSION_SHA}</Text>
      </View>
    );
  },
  SignIn: {
    Footer() {
      const { tokens } = useTheme();
      const { toSignUp, toResetPassword } = useAuthenticator();
      return (
        <View textAlign="center" padding={tokens.space.large} paddingTop={0}>
          <Button onClick={toSignUp}>
            Are you a teacher? Create a free account!
          </Button>
          <UIButton
            marginTop={tokens.space.large}
            fontWeight="normal"
            onClick={toResetPassword}
            size="small"
            variation="link"
          >
            Forgot password?
          </UIButton>
        </View>
      );
    },
  },
  SignUp: {
    Header() {
      const { tokens } = useTheme();
      return (
        <View padding={tokens.space.large} paddingBottom={0}>
          {invited ? (
            <Header textAlign="center">New User Invitation Sign Up</Header>
          ) : (
            <>
              <Message icon warning>
                <Icon name="warning sign" />
                <Message.Content>
                  <Message.Header>Are you a parent?</Message.Header>
                  If you are a parent looking to sign up to access reports for
                  your student, you should have received an email invitation
                  from your teacher. If you sign up here, you will not be able
                  to access your student's reports.
                </Message.Content>
              </Message>
              <Header textAlign="center">Create Teacher Account</Header>
            </>
          )}
        </View>
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();
      const { tokens } = useTheme();
      return !invited ? (
        <View textAlign="center" padding={tokens.space.large} paddingTop={0}>
          <Button onClick={toSignIn}>Back to Sign In</Button>
        </View>
      ) : (
        <View padding={tokens.space.large} paddingTop={0}>
          <Message info>
            <Message.Content>
              <Message.Header>Already received a code?</Message.Header>
              Go to the
              <button className="link-button" onClick={toSignIn}>
                login page
              </button>{" "}
              and enter your credentials to enter the code to finish creating
              your account.
            </Message.Content>
          </Message>
        </View>
      );
    },
  },
});

const Main = () => {
  const invited = "invitation" in clientMetadata;
  const services = {
    async handleSignUp(formData) {
      console.log("handleSignUp", formData);
      let { username, password } = formData;
      let params = {
        username,
        password,
      };
      params.username = await getUsername(username);
      if (invited) {
        params = {
          ...params,
          clientMetadata,
          attributes: {
            "custom:invitation": clientMetadata.invitation,
          },
        };
      }
      console.log("params", params);
      return Auth.signUp(params);
    },
    async handleForgotPassword(username) {
      console.log("Forgot Password", username);
      const email = await getUsername(username);
      console.log(email);
      return Auth.forgotPassword(email);
    },
    async handleForgotPasswordSubmit(formData) {
      const { username, code, password } = formData;
      console.log("Forgot Password Submit", formData);
      const email = await getUsername(username);
      return Auth.forgotPasswordSubmit(email, code, password);
    },
    async handleConfirmSignUp(formData) {
      let { username, code } = formData;
      const email = await getUsername(username);
      return Auth.confirmSignUp(email, code, {
        clientMetadata,
      });
    },
    async handleSignIn(formData) {
      let { username, password } = formData;
      const email = await getUsername(username);
      console.log(email);
      return Auth.signIn({
        username: email,
        password,
        validationData: {
          version: process.env.REACT_APP_VERSION,
        },
      });
    },
  };

  console.log("Main");
  return (
    <div className={invited ? "invited" : ""}>
      <Authenticator
        loginMechanisms={["email"]}
        services={services}
        initialState={initialState}
        formFields={{
          signUp: {
            email: {
              defaultValue: defaultEmail,
              isReadOnly: invited,
              hidden: invited,
            },
            ignored: {
              order: 1,
              defaultValue: defaultEmail,
              disabled: invited,
              hidden: !invited,
            },
          },
        }}
        components={getComponents(invited)}
      >
        {({ signOut, user }) => (
          <AppSyncApolloProvider>
            <AppWithUser user={user} signOut={signOut} />
          </AppSyncApolloProvider>
        )}
      </Authenticator>
    </div>
  );
};

ReactDOM.render(<Main />, document.getElementById("root"));
